/**
 * 문전약국 상세·수정 화면.
 * admin-ruda-web `pharmacyInfoDetails.js` 포팅.
 * - 약국명 필수
 * - 비고(note)는 레거시와 동일하게 수정 가능
 */
import {
  usePharmacyDetail,
  // useUpdatePharmacy,
} from '@/entities/pharmacy/hooks/usePharmacies';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { AddressSearchField } from '@/shared/components/AddressSearchField';
import { FormTextField } from '@/shared/components/FormTextField';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { FooterRow } from '@/shared/components/ui/row';
// import { Textarea } from '@/shared/components/ui/textarea';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { Badge } from '@/shared/components/ui/badge';
import { formatClosedOnDisplay } from '@/shared/utils/facilityClosure';
import {
  getPartnerBusinessStatusBadgeVariant,
  getPartnerBusinessStatusLabel,
} from '@/shared/utils/partnerBusinessStatus';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Store } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const pharmacyDetailSchema = z.object({
  pharmacyUniqueCode: z.string(),
  pharmacyName: z
    .string()
    .trim()
    .min(1, '약국명을 입력해 주세요'),
  address: z.string(),
  contactNumber: z.string(),
  remarks: z.string(),
});

type PharmacyDetailFormValues = z.infer<typeof pharmacyDetailSchema>;

export const PharmacyDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const { pharmacyId: pharmacyIdParam } = useParams({ strict: false }) as {
    pharmacyId?: string;
  };
  const pharmacyId = Number(pharmacyIdParam);

  // const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);
  // 저장 복구 시 좌표와 함께 사용
  // const [coords, setCoords] = useState<{
  //   longitude?: number;
  //   latitude?: number;
  // }>({});

  const { data, isLoading, isError, error } = usePharmacyDetail(
    Number.isFinite(pharmacyId) ? pharmacyId : null,
  );
  // const updateMutation = useUpdatePharmacy();

  const form = useForm<PharmacyDetailFormValues>({
    resolver: zodResolver(pharmacyDetailSchema),
    defaultValues: {
      pharmacyUniqueCode: '',
      pharmacyName: '',
      address: '',
      contactNumber: '',
      remarks: '',
    },
  });

  /** 편집 중 refetch가 form.reset으로 비고 등 로컬 수정을 덮지 않도록 */
  const loadedPharmacyIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!data) return;

    const isNewPharmacy = loadedPharmacyIdRef.current !== pharmacyId;
    if (!isNewPharmacy && form.formState.isDirty) return;

    form.reset({
      pharmacyUniqueCode: data.pharmacyUniqueCode ?? '',
      pharmacyName: data.pharmacyName ?? '',
      address: data.address ?? '',
      contactNumber: data.contactNumber ?? '',
      remarks: data.remarks ?? '',
    });
    // setCoords({
    //   longitude: data.longitude,
    //   latitude: data.latitude,
    // });
    loadedPharmacyIdRef.current = pharmacyId;
  }, [data, form, pharmacyId]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const handleBack = () => {
    if (form.formState.isDirty) {
      setQuitDialogOpen(true);
      return;
    }
    navigate({ to: ROUTE_PATHS.PHARMACIES } as never);
  };

  // 저장 버튼 일시 비활성화 — 복구 시 함께 복원
  // const handleSave = async (values: PharmacyDetailFormValues) => {
  //   if (!Number.isFinite(pharmacyId)) return;
  //   try {
  //     await updateMutation.mutateAsync({
  //       pharmacyId,
  //       data: {
  //         pharmacyName: values.pharmacyName,
  //         address: values.address || undefined,
  //         contactNumber: values.contactNumber || undefined,
  //         remarks: values.remarks || undefined,
  //         longitude: coords.longitude,
  //         latitude: coords.latitude,
  //       },
  //     });
  //     toast.success('약국 정보가 성공적으로 저장되었습니다.');
  //     form.reset(values);
  //     setSaveDialogOpen(false);
  //   } catch (err) {
  //     handleApiError(err, toast);
  //   }
  // };

  if (!Number.isFinite(pharmacyId)) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 약국 ID입니다.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="약국 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        약국 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Store}
        title="문전약국 상세"
        description={data.pharmacyName}
        actions={
          <FooterRow gap={8}>
            {/* 저장 — 일시 비활성화
            <Button
              type="button"
              onClick={form.handleSubmit(() => setSaveDialogOpen(true))}
              disabled={!form.formState.isDirty || updateMutation.isPending}
              className="ruda-button"
            >
              저장
            </Button>
            */}
            <Button type="button" variant="outline" onClick={handleBack}>
              나가기
            </Button>
          </FooterRow>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{data.pharmacyName}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              className="space-y-6"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="grid gap-4 md:grid-cols-3">
                <FormTextField
                  control={form.control}
                  name="pharmacyUniqueCode"
                  label="약국 고유번호"
                  disabled
                />
                <FormTextField
                  control={form.control}
                  name="pharmacyName"
                  label="약국명"
                  required
                  placeholder="약국명"
                  disabled={readOnly}
                />
                <FormTextField
                  control={form.control}
                  name="contactNumber"
                  label="전화번호"
                  disabled={readOnly}
                />
                <div className="space-y-2">
                  <p className="text-sm font-medium leading-none">영업 상태</p>
                  <div className="flex h-10 items-center">
                    <Badge
                      variant={getPartnerBusinessStatusBadgeVariant(
                        data.businessStatusType,
                      )}
                    >
                      {getPartnerBusinessStatusLabel(data.businessStatusType)}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium leading-none">폐업일</p>
                  <p className="rounded-md border bg-muted px-3 py-2 text-sm">
                    {formatClosedOnDisplay(data.closedOn)}
                  </p>
                </div>
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>주소</FormLabel>
                    <AddressSearchField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      disabled={readOnly}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 비고 — 일시 비활성화
              <FormField
                control={form.control}
                name="remarks"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>비고</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="비고를 입력해 주세요"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              */}
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* 저장 확인 다이얼로그 — 저장 버튼과 함께 일시 비활성화
      <AlertDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장 확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 약국 정보를 저장합니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={form.handleSubmit(handleSave)}
              disabled={updateMutation.isPending}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      */}

      <AlertDialog open={quitDialogOpen} onOpenChange={setQuitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              저장하지 않은 변경 사항이 있습니다. 목록으로 돌아가시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>계속 편집</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigate({ to: ROUTE_PATHS.PHARMACIES } as never)}
            >
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
